import { Expose, Type } from 'class-transformer';
import { ContactWithRoleResponseDto } from '../../contact/contact-with-role.response.dto';
import { SopResponseDto } from '../../sop/sop.response.dto';
import { ShippingProcessConfigResponseDto } from '../shipping-process-config/shipping-process-config.response.dto';

export class WarehouseDetailsResponseDto {
    /**
     * Unique identifier for the warehouse details
     * @example '123e4567-e89b-12d3-a456-426614174000'
     */
    @Expose()
    id: string;

    /**
     * Reference to the client information record
     * @example '123e4567-e89b-12d3-a456-426614174001'
     */
    @Expose()
    clientInformationRecordId: string;

    /**
     * Reference to the organization that owns the warehouse
     * @example '123e4567-e89b-12d3-a456-426614174002'
     */
    @Expose()
    organizationId: string;

    /**
     * Name of the warehouse facility
     * @example 'Main Distribution Center'
     */
    @Expose()
    warehouseName: string;

    /**
     * Type of storage arrangement
     * @example 'OWN_WAREHOUSE'
     */
    @Expose()
    storageType: string;

    /**
     * Physical address of the warehouse
     * @example '123 Industrial Ave, Toronto, ON M1B 2C3'
     */
    @Expose()
    warehouseAddress: string;

    /**
     * Website URL of the warehouse facility
     * @example 'www.warehouse-solutions.com'
     */
    @Expose()
    warehouseWebsite: string;

    /**
     * Contact person ID for the warehouse
     * @example '123e4567-e89b-12d3-a456-426614174003'
     */
    @Expose()
    warehouseContactId: string;

    /**
     * Is warehouse on Client/Importer DEL annex?
     * @example true
     */
    @Expose()
    clientImporterDelAnnex: boolean;

    /**
     * Does 3PL have its own QMS?
     * @example true
     */
    @Expose()
    hasOwnQms: boolean;

    /**
     * Does 3PL use Q&C QMS?
     * @example false
     */
    @Expose()
    usesQncQms: boolean;

    /**
     * Vendor for temperature monitoring
     * @example 'TempTracker Inc'
     */
    @Expose()
    tempMonitoringVendor: string;

    /**
     * Temperature monitoring report frequency
     * @example 'Monthly'
     */
    @Expose()
    tempMonitoringReportFrequency: string;

    /**
     * Automated reports with alerts & alarms emailed?
     * @example true
     */
    @Expose()
    tempMonitoringAutomatedAlerts: boolean;

    /**
     * Client downloads and sends reports?
     * @example false
     */
    @Expose()
    tempMonitoringClientDownloads: boolean;

    /**
     * Reviewed during Self Inspection?
     * @example true
     */
    @Expose()
    tempMonitoringSelfInspection: boolean;

    /**
     * Who performs sanitation
     * @example 'Warehouse staff'
     */
    @Expose()
    sanitationPerformedBy: string;

    /**
     * Sanitation self-inspection frequency
     * @example 'Quarterly'
     */
    @Expose()
    sanitationSelfInspectionFreq: string;

    /**
     * Reviewed during Self Inspection?
     * @example true
     */
    @Expose()
    sanitationSelfInspection: boolean;

    /**
     * Client downloads and sends reports?
     * @example false
     */
    @Expose()
    sanitationClientDownloads: boolean;

    /**
     * Vendor for pest control
     * @example 'Orkin'
     */
    @Expose()
    pestControlVendor: string;

    /**
     * Pest control self-inspection frequency
     * @example 'Monthly'
     */
    @Expose()
    pestControlSelfInspectionFreq: string;

    /**
     * Reviewed during Self Inspection?
     * @example true
     */
    @Expose()
    pestControlSelfInspection: boolean;

    /**
     * Client downloads and sends reports?
     * @example false
     */
    @Expose()
    pestControlClientDownloads: boolean;

    /**
     * Automated reports with alerts & alarms emailed?
     * @example true
     */
    @Expose()
    pestControlAutomatedAlerts: boolean;

    /**
     * Whether the warehouse has temperature control
     * @example true
     */
    @Expose()
    temperatureControlled: boolean;

    /**
     * Temperature range maintained in the warehouse
     * @example '15-25°C'
     */
    @Expose()
    temperatureRange: string;

    /**
     * Whether the warehouse has GMP certification
     * @example true
     */
    @Expose()
    gmpCertified: boolean;

    /**
     * GMP certification number
     * @example 'GMP-2024-001'
     */
    @Expose()
    gmpCertificationNumber: string;

    /**
     * Whether the warehouse has GDP certification
     * @example true
     */
    @Expose()
    gdpCertified: boolean;

    /**
     * GDP certification number
     * @example 'GDP-2024-001'
     */
    @Expose()
    gdpCertificationNumber: string;

    /**
     * Special handling requirements for products
     * @example 'Cold chain storage for temperature-sensitive products'
     */
    @Expose()
    specialHandlingRequirements: string;

    /**
     * Additional notes about the warehouse
     * @example 'Temperature-controlled facility with specialized cold storage area'
     */
    @Expose()
    notes: string;

    @Expose()
    @Type(() => ContactWithRoleResponseDto)
    contacts?: ContactWithRoleResponseDto[];

    /**
     * SOPs associated with this warehouse
     * @example [{ sopNumber: 'W2-01', sopTitle: 'Receipt of Product' }]
     */
    @Expose()
    @Type(() => SopResponseDto)
    sops?: SopResponseDto[];

    /**
     * Shipping process configurations for this warehouse
     * @example [{ direction: 'INBOUND', containerOrVehicleType: 'Topa Thermal' }]
     */
    @Expose()
    @Type(() => ShippingProcessConfigResponseDto)
    shippingProcessConfigs?: ShippingProcessConfigResponseDto[];

    /**
     * Timestamp when the record was created
     * @example '2024-01-15T10:30:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    createdAt: Date;

    /**
     * Timestamp when the record was last updated
     * @example '2024-01-15T15:45:00.000Z'
     */
    @Expose()
    @Type(() => Date)
    updatedAt: Date;

    /**
     * Timestamp when the record was deleted (soft delete)
     * @example null
     */
    @Expose()
    @Type(() => Date)
    deletedAt: Date | null;
}
